/* ============================================================================
 * DailyBreakdown — day-by-day table of page views, App Store clicks
 * (all + short-link), and Apple installs. Range selector: 7 / 14 / 30 / 90.
 * Purpose: reconcile stableish counts against Instagram Ads / TikTok / etc.
 * ==========================================================================*/
export function DailyBreakdown({ data, days, onDaysChange }) {
  const rows = data?.rows ?? [];
  const ranges = [7, 14, 30, 90];

  const totals = rows.reduce(
    (acc, r) => ({
      page_views: acc.page_views + (r.page_views || 0),
      download_clicks: acc.download_clicks + (r.download_clicks || 0),
      short_link_clicks: acc.short_link_clicks + (r.short_link_clicks || 0),
      installs: acc.installs + (r.installs || 0),
    }),
    { page_views: 0, download_clicks: 0, short_link_clicks: 0, installs: 0 }
  );

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso + "T00:00:00Z");
      return d.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });
    } catch {
      return iso;
    }
  };

  return (
    <section
      className="border border-white/10 bg-[#0a0a0a] p-6"
      data-testid="admin-daily-breakdown"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-1">
            // day by day
          </div>
          <h3 className="font-display text-xl font-extrabold tracking-tight">
            Daily breakdown
          </h3>
        </div>
        <div className="flex items-center gap-1" data-testid="daily-range-selector">
          {ranges.map((r) => (
            <button
              key={r}
              onClick={() => onDaysChange(r)}
              data-testid={`daily-range-${r}`}
              className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2.5 py-1 border transition-colors ${
                days === r
                  ? "bg-[#FF2A2A] border-[#FF2A2A] text-white"
                  : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
              }`}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="border-b border-white/10">
            <tr className="text-left font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
              <th className="py-2 pr-4">Date</th>
              <th className="py-2 pr-4 text-right">Page views</th>
              <th className="py-2 pr-4 text-right">Clicks (all)</th>
              <th className="py-2 pr-4 text-right">Short-link</th>
              <th className="py-2 pr-0 text-right">Installs</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="py-8 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-white/35"
                >
                  no data yet
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const total = r.page_views + r.download_clicks + r.installs;
              return (
                <tr
                  key={r.date}
                  data-testid={`daily-row-${r.date}`}
                  className={`border-t border-white/5 ${total === 0 ? "text-white/30" : "text-white/85"}`}
                >
                  <td className="py-2 pr-4 font-mono text-xs">{fmtDate(r.date)}</td>
                  <td className="py-2 pr-4 text-right font-mono text-xs">{r.page_views || "—"}</td>
                  <td className={`py-2 pr-4 text-right font-mono text-xs ${r.download_clicks > 0 ? "text-white" : ""}`}>
                    {r.download_clicks || "—"}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-xs">
                    {r.short_link_clicks || "—"}
                  </td>
                  <td className={`py-2 pr-0 text-right font-mono text-xs ${r.installs > 0 ? "text-[#FF2A2A]" : ""}`}>
                    {r.installs || "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-white/20 font-mono text-xs">
              <td className="py-3 pr-4 uppercase tracking-[0.22em] text-white/60 text-[10px]">
                {days}d total
              </td>
              <td className="py-3 pr-4 text-right text-white">{totals.page_views.toLocaleString()}</td>
              <td className="py-3 pr-4 text-right text-white">{totals.download_clicks.toLocaleString()}</td>
              <td className="py-3 pr-4 text-right text-white/70">{totals.short_link_clicks.toLocaleString()}</td>
              <td className="py-3 pr-0 text-right text-[#FF2A2A]">{totals.installs.toLocaleString()}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="mt-5 border-l-2 border-white/15 pl-4 py-2 text-xs text-white/55 leading-relaxed">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/70 mb-1">
          why this differs from instagram ads
        </p>
        <p>
          Instagram counts a click the moment someone taps your link — even if they
          bail before the browser finishes loading. Our numbers only count visitors
          whose browser fully loaded stableish.me. Expect Instagram to show <strong>2&ndash;4&times;</strong> more
          clicks than <strong>&ldquo;Page views&rdquo;</strong> here. If IG shows 100 clicks and we show 40 page
          views, that&rsquo;s normal &mdash; the missing 60 tapped &amp; abandoned.
        </p>
        <p className="mt-2">
          The <strong className="text-white/75">Short-link</strong> column shows clicks through your{" "}
          <code className="font-mono text-[#FF2A2A]">/ig</code>,{" "}
          <code className="font-mono text-[#FF2A2A]">/tw</code> etc. redirects &mdash; those
          go straight to the App Store, so they&rsquo;re your <strong>truest &ldquo;wanted to download&rdquo;</strong> signal.
        </p>
      </div>
    </section>
  );
}
